import type { IListenable } from "../../../interfaces/IListenable";
import type { IMeasurable } from "../../../interfaces/IMeasurable";
import { Node } from "../../Core/Node";
import { EventEmitter } from "../../Core/EventEmitter";
import { QuadTree } from "../../Core/QuadTree";
import { BaseDraggable, DragEventData, MouseButton } from "./BaseDraggable";
import { EventHandler } from "../../../interfaces/EventHandler";

const USE_QUADTREE = true; // 是否使用四叉树优化性能
const CHECK_BOUNDARIES = true; // 是否检查边界

/**
 * 可拖拽项的内部表示
 */
type DraggableItem = {
    node: Node;
    dragCursor: string;
}

/**
 * DraggableManager 发射的事件类型
 */
export enum DragEvents {
    /** 拖拽节点开始事件，抛出 {@link DragEventData} 类型事件对象 */
    NODE_START = 'drag-node-start',
    /** 拖拽节点中事件，抛出 {@link DragEventData} 类型事件对象 */
    NODE = 'drag-node',
    /** 拖拽节点结束事件，抛出 {@link DragEventData} 类型事件对象 */
    NODE_END = 'drag-node-end',
    /** 拖拽画布开始事件，抛出 {@link DragEventData} 类型事件对象 */
    CANVAS_START = 'drag-canvas-start',
    /** 拖拽画布中事件，抛出 {@link DragEventData} 类型事件对象 */
    CANVAS = 'drag-canvas',
    /** 拖拽画布结束事件，抛出 {@link DragEventData} 类型事件对象 */
    CANVAS_END = 'drag-canvas-end'
}

/**
 * DraggableManager 事件映射，用于类型提示
 */
interface DragEventMap {
    [DragEvents.NODE_START]: DragEventData;
    [DragEvents.NODE]: DragEventData;
    [DragEvents.NODE_END]: DragEventData;
    [DragEvents.CANVAS_START]: DragEventData;
    [DragEvents.CANVAS]: DragEventData;
    [DragEvents.CANVAS_END]: DragEventData;
}

/**
 * 管理场景中所有可拖拽对象的管理器
 * - 支持拖拽单个节点
 * - 支持拖拽整个画布
 * - 通过事件发射器对外通知拖拽状态
 */
export class DraggableManager extends BaseDraggable {

    /** 存储所有可拖拽项的 Map，键为 Node 对象 */
    private items: Map<Node, DraggableItem> = new Map();
    /** 当前正在被拖拽的项，如果为 null 则表示拖拽整个画布 */
    private activeItem: DraggableItem | null = null;
    /** 统一的事件发射器，用于对外广播拖拽事件 */
    private managerEvents = new EventEmitter<DragEventMap>();
    /** 
     * 设置拖拽单个节点的按钮数组 
     * @default MouseButton.Left
     */
    private dragNode: MouseButton[] | boolean;
    /** 
     * 设置拖拽整个区域的按钮数组
     * @default MouseButton.Right
     */
    private dragAll: MouseButton[] | boolean;

    // 添加四叉树成员
    // private quadTree: QuadTree;
    // private needsQuadTreeRebuild: boolean = false;

    /**
     * DraggableManager 构造函数
     * @param target 监听事件的 DOM 元素包装器
     * @param options 配置选项
     */
    constructor(
        target?: IListenable & IMeasurable,
        options?: {
            dragNode?: MouseButton[] | MouseButton | boolean;
            dragAll?: MouseButton[] | MouseButton | boolean;
            useThrottle?: boolean; // 是否使用节流函数
            throttleWait?: number; // 节流函数的等待时间
        }
    ) {
        const { dragNode, dragAll, useThrottle, throttleWait } = options || {};

        super(target, { useThrottle, throttleWait }); // 调用父类构造函数

        // 设置鼠标按钮
        this.dragNode = this.validateMouseButtonOption(dragNode, [MouseButton.Left]);
        this.dragAll = this.validateMouseButtonOption(dragAll, [MouseButton.Right]);

        // 确保两个按钮集合没有交集
        this.validateButtonSets();

        // 初始化四叉树
        // const bounds = this.getQuadTreeBounds();
        // this.quadTree = new QuadTree(bounds);
    }

    // #region Public API Methods

    /**
     * 监听一个拖拽事件
     * @param eventName 事件名称
     * @param listener 事件回调函数
     */
    on(eventName: DragEvents, listener: EventHandler): this {
        this.managerEvents.on(eventName, listener);
        return this;
    }

    /**
     * 取消监听一个拖拽事件
     * @param eventName 事件名称
     * @param listener 事件回调函数
     */
    off(eventName: DragEvents, listener: EventHandler): this {
        this.managerEvents.off(eventName, listener);
        return this;
    }

    /**
     * 监听一个只执行一次的拖拽事件
     * @param eventName 事件名称
     * @param listener 事件回调函数
     */
    once(eventName: DragEvents, listener: EventHandler): this {
        this.managerEvents.once(eventName, listener);
        return this;
    }

    /**
     * 添加一个可拖拽节点
     * @param node 要添加的节点
     * @param options 相关配置，如拖拽时的光标样式
     */
    add(node: Node, options?: { dragCursor?: string }): void {
        if (this.items.has(node)) {
            // 如果已存在，可以更新选项或直接返回
            const item = this.items.get(node)!;
            item.dragCursor = options?.dragCursor || item.dragCursor || 'move';
            return;
        }

        const item: DraggableItem = {
            node,
            dragCursor: options?.dragCursor || 'move',
        };

        this.items.set(node, item);
    }

    /**
     * 批量添加可拖拽节点
     * @param nodes 要添加的节点数组
     * @param options 相关配置
     */
    addBatch(nodes: Node[], options?: { dragCursor?: string }): void {
        for (const node of nodes) {
            this.add(node, options); // 直接调用 add
        }
    }

    /**
     * 移除一个可拖拽节点
     * @param node 要移除的节点
     */
    remove(node: Node): void {
        const item = this.items.get(node);
        if (item) {
            if (this.activeItem === item) {
                this.isDragging = false;
                document.body.style.cursor = this.originalCursor;
                this.activeItem = null;
                this.activeButton = null;
            }
            this.items.delete(node);
        }
    }

    /**
     * 设置指定节点在拖拽时的光标样式
     * @param node 目标节点
     * @param cursor 光标样式字符串
     */
    setElementDragCursor(node: Node, cursor: string): void {
        const item = this.items.get(node);
        if (item) item.dragCursor = cursor;
    }

    /**
     * 修改用于拖拽节点的鼠标按钮
     * @param buttons 鼠标按钮或按钮数组，或一个布尔值来启用/禁用
     */
    setDragNodeButtons(buttons: MouseButton[] | MouseButton | boolean): void {
        this.dragNode = this.validateMouseButtonOption(buttons, [MouseButton.Left]);
        this.validateButtonSets();
    }

    /**
     * 修改用于拖拽画布的鼠标按钮
     * @param buttons 鼠标按钮或按钮数组，或一个布尔值来启用/禁用
     */
    setDragAllButtons(buttons: MouseButton[] | MouseButton | boolean): void {
        this.dragAll = this.validateMouseButtonOption(buttons, [MouseButton.Right]);
        this.validateButtonSets();
    }

    /**
     * 重写 dispose 方法以销毁管理器，清理所有资源和事件监听器
     */
    override dispose(): void {
        super.dispose();
        this.managerEvents.removeAllListeners(); // 清理 managerEvents 的所有监听器
        this.items.clear();
    }

    // #endregion Public API Methods

    // #region Protected Event Handlers

    /**
     * 处理鼠标按下事件，判断并启动节点或画布拖拽
     * @param e 鼠标事件
     */
    protected override handleMouseDown(e: MouseEvent): void {
        if (this.dragNode === false && this.dragAll === false) return;
        if (this.isDragging) return;

        const { x: mouseX, y: mouseY } = this.getRelativeMousePosition(e);
        const buttonPressed = e.button as MouseButton;

        // 拖拽单个元素
        if (this.isButtonAllowed(buttonPressed, this.dragNode)) {
            // 查找点击位置下的元素
            for (const item of this.items.values()) {
                if (this.isPointInNode(mouseX, mouseY, item.node)) {
                    this.startNodeDrag(e, item);
                    break;
                }
            }
        }
        // 拖拽整个区域
        else if (this.isButtonAllowed(buttonPressed, this.dragAll)) {
            this.startCanvasDrag(e);
        }
    }

    /**
     * 处理鼠标移动事件，分发到节点或画布的拖拽处理函数
     * @param e 鼠标事件
     */
    protected override handleMouseMove(e: MouseEvent): void {
        if (!this.isDragging) return;

        // 移动单个元素
        if (this.isButtonAllowed(this.activeButton as MouseButton, this.dragNode) && this.activeItem) {
            this.handleNodeDrag(e);
        }
        // 移动所有元素
        else if (this.isButtonAllowed(this.activeButton as MouseButton, this.dragAll) && this.activeItem === null) {
            this.handleCanvasDrag(e);
        }
    }

    /**
     * 处理鼠标松开事件，结束拖拽状态
     * @param e 鼠标事件
     */
    protected override handleMouseUp(e: MouseEvent): void {
        if (!this.isDragging || e.button !== this.activeButton) return;

        // 恢复光标样式
        document.body.style.cursor = this.originalCursor;

        // 根据拖拽类型调用相应的结束处理函数
        if (this.isButtonAllowed(this.activeButton as MouseButton, this.dragNode) && this.activeItem) {
            this.endNodeDrag(e);
        } else if (this.isButtonAllowed(this.activeButton as MouseButton, this.dragAll) && this.activeItem === null) {
            this.endCanvasDrag(e);
        }

        // 重置拖拽状态
        this.isDragging = false;
        this.activeItem = null;
        this.activeButton = null;
    }

    // #endregion Protected Event Handlers

    // #region Private Drag Logic

    /**
     * 开始节点拖拽的初始化逻辑
     * @param e 鼠标事件
     * @param item 被拖拽的节点项
     */
    private startNodeDrag(e: MouseEvent, item: DraggableItem): void {
        const { x: mouseX, y: mouseY } = this.getRelativeMousePosition(e);

        this.isDragging = true;
        this.activeButton = e.button as MouseButton;
        this.activeItem = item;
        this.mouseX = mouseX;
        this.mouseY = mouseY;
        this.lastX = mouseX;
        this.lastY = mouseY;

        // 保存并设置光标
        this.originalCursor = document.body.style.cursor;
        document.body.style.cursor = item.dragCursor;

        this.emitDragEvent(DragEvents.NODE_START, {
            originalEvent: e,
            node: item.node,
        });

        e.preventDefault();
    }

    /**
     * 处理节点拖拽中的位置更新
     * @param e 鼠标事件
     */
    private handleNodeDrag(e: MouseEvent): void {
        if (!this.activeItem) return;

        const { x: mouseX, y: mouseY } = this.getRelativeMousePosition(e);
        const deltaX = mouseX - this.lastX;
        const deltaY = mouseY - this.lastY;
        const node = this.activeItem.node;

        // 应用移动前检查边界
        const { x: adjustedDeltaX, y: adjustedDeltaY } = this.checkBoundaries(deltaX, deltaY, node);

        this.emitDragEvent(DragEvents.NODE, {
            originalEvent: e,
            deltaX: adjustedDeltaX,
            deltaY: adjustedDeltaY,
            node: node,
        });

        this.lastX = mouseX;
        this.lastY = mouseY;
    }

    /**
     * 结束节点拖拽
     * @param e 鼠标事件
     */
    private endNodeDrag(e: MouseEvent): void {
        if (!this.activeItem) return;

        this.emitDragEvent(DragEvents.NODE_END, {
            originalEvent: e,
            node: this.activeItem.node
        });
    }

    /**
     * 开始画布拖拽的初始化逻辑
     * @param e 鼠标事件
     */
    private startCanvasDrag(e: MouseEvent): void {
        const { x: mouseX, y: mouseY } = this.getRelativeMousePosition(e);

        this.isDragging = true;
        this.activeButton = e.button as MouseButton;
        this.activeItem = null;
        this.mouseX = mouseX;
        this.mouseY = mouseY;
        this.lastX = mouseX;
        this.lastY = mouseY;

        this.originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'grabbing';

        this.emitDragEvent(DragEvents.CANVAS_START, {
            originalEvent: e,
        });

        e.preventDefault();
    }

    /**
     * 处理画布拖拽中的位置更新
     * @param e 鼠标事件
     */
    private handleCanvasDrag(e: MouseEvent): void {
        const { x: mouseX, y: mouseY } = this.getRelativeMousePosition(e);
        const deltaX = mouseX - this.lastX;
        const deltaY = mouseY - this.lastY;

        this.emitDragEvent(DragEvents.CANVAS, {
            originalEvent: e,
            deltaX: deltaX,
            deltaY: deltaY,
        });

        this.lastX = mouseX;
        this.lastY = mouseY;
    }

    /**
     * 结束画布拖拽
     * @param e 鼠标事件
     */
    private endCanvasDrag(e: MouseEvent): void {
        this.emitDragEvent(DragEvents.CANVAS_END, {
            originalEvent: e
        });
    }

    // #endregion Private Drag Logic

    // #region Private Helpers

    /**
     * 统一发射拖拽事件
     * @param eventType 事件类型
     * @param data 事件相关数据
     */
    private emitDragEvent(eventType: DragEvents, data: {
        originalEvent: MouseEvent;
        deltaX?: number;
        deltaY?: number;
        node?: Node;
    }): void {
        this.managerEvents.emit(eventType, {
            originalEvent: data.originalEvent,
            button: this.activeButton,
            mouseX: this.mouseX,
            mouseY: this.mouseY,
            lastX: this.lastX,
            lastY: this.lastY,
            deltaX: data.deltaX,
            deltaY: data.deltaY,
            node: data.node,
        } as DragEventData);
    }

    /**
     * 获取鼠标相对容器左上角的坐标
     * @param e 鼠标事件
     * @returns 相对坐标对象 { x, y }
     */
    private getRelativeMousePosition(e: MouseEvent): { x: number, y: number } {
        const containerRect = this.domWrapper.getBoundingClientRect();
        return {
            x: e.clientX - containerRect.left,
            y: e.clientY - containerRect.top
        };
    }

    /**
     * 验证并格式化鼠标按钮配置
     * @param option 用户传入的鼠标按钮选项
     * @param defaultButtons 默认按钮配置
     * @returns 验证后的按钮数组或布尔值 false
     */
    private validateMouseButtonOption(
        option: MouseButton[] | MouseButton | boolean | undefined,
        defaultButtons: MouseButton[]
    ): MouseButton[] | boolean {
        // 如果未定义，使用默认值
        if (option === undefined) {
            return defaultButtons;
        }

        // 如果是布尔值false，则禁用此功能
        if (option === false) {
            return false;
        }

        // 如果是布尔值true，则使用默认按键
        if (option === true) {
            return defaultButtons;
        }

        // 如果是单个按钮，转换为数组
        if (typeof option === 'number') {
            if (![MouseButton.Left, MouseButton.Middle, MouseButton.Right].includes(option)) {
                console.warn(`无效的鼠标按钮值 ${option}，将使用默认值`);
                return defaultButtons;
            }
            return [option];
        }

        // 如果是数组，验证每个元素
        if (Array.isArray(option)) {
            // 筛选出有效的按钮
            const validButtons = option.filter(btn =>
                [MouseButton.Left, MouseButton.Middle, MouseButton.Right].includes(btn)
            );

            // 去除重复值
            const uniqueButtons = [...new Set(validButtons)];

            if (uniqueButtons.length === 0) {
                console.warn(`没有提供有效的鼠标按钮，将使用默认值`);
                return defaultButtons;
            }

            return uniqueButtons;
        }

        // 其他情况返回默认值
        console.warn(`无效的鼠标按钮设置，将使用默认值`);
        return defaultButtons;
    }

    /**
     * 检查给定的按钮是否在允许的按钮集合中
     * @param button 当前按下的按钮
     * @param allowedButtons 允许的按钮配置
     */
    private isButtonAllowed(button: MouseButton, allowedButtons: MouseButton[] | boolean): boolean {
        if (allowedButtons === false) return false;
        if (allowedButtons === true) return true;
        return allowedButtons.includes(button);
    }

    /**
     * 验证并确保拖拽节点和拖拽画布的按钮集合没有交集
     * 如果有冲突，优先保留拖拽节点的设置
     */
    private validateButtonSets(): void {
        // 如果任一为布尔值false，无需进一步检查
        if (this.dragNode === false || this.dragAll === false) {
            return;
        }

        // 如果两者都是数组
        if (Array.isArray(this.dragNode) && Array.isArray(this.dragAll)) {
            // 使用局部变量来帮助 TypeScript 正确推断类型
            const currentDragNode = this.dragNode; // currentDragNode 类型为 MouseButton[]
            let currentDragAll = this.dragAll;   // currentDragAll 类型为 MouseButton[]

            // 确保 dragNode 和 dragAll 没有重叠的按钮，若有冲突则优先保留 dragNode 的设置
            const overlap = currentDragNode.filter(btn => currentDragAll.includes(btn));

            if (overlap.length > 0) {
                console.warn(`检测到 dragNode 和 dragAll 有重叠的按钮: ${overlap.join(', ')}，将从 dragAll 中移除`);

                // 从 dragAll 中移除重叠的按钮
                currentDragAll = currentDragAll.filter(btn => !overlap.includes(btn));

                // 如果 dragAll 变为空数组，设置默认值
                if (currentDragAll.length === 0) {
                    // 尝试找一个未使用的按钮
                    const availableButtons = [
                        MouseButton.Left,
                        MouseButton.Middle,
                        MouseButton.Right
                    ].filter(btn => !currentDragNode.includes(btn)); // 使用 currentDragNode

                    if (availableButtons.length > 0) {
                        this.dragAll = [availableButtons[0]];
                    } else {
                        // 如果所有按钮都被 dragNode 使用，禁用 dragAll
                        console.warn(`所有鼠标按钮都被 dragNode 使用，dragAll 将被禁用`);
                        this.dragAll = false;
                    }
                } else {
                    // 更新 this.dragAll 为修改后的数组
                    this.dragAll = currentDragAll;
                }
            }
            // 如果 overlap.length === 0，则 this.dragAll (此时为 currentDragAll) 保持不变，它已经是数组类型
        }
    }

    // #endregion Private Helpers
}